// src/pages/ProjectControl.tsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Typography,
  Input,
  Button,
  Tabs,
  Table,
  Modal,
  Form,
  Select,
  Tag,
  message,
  Divider,
  Space,
  Flex,
  Tooltip,
  Dropdown,
  Drawer,
  Grid,
  theme,
  Checkbox,
} from "antd";
import {
  FolderOutlined,
  ProfileOutlined,
  CommentOutlined,
  ShareAltOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  HistoryOutlined,
  EyeOutlined,
  RollbackOutlined,
  MoreOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// --- MDXEditor Imports ---
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  linkPlugin,
  linkDialogPlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  ListsToggle,
  CreateLink,
  InsertThematicBreak,
} from "@mdxeditor/editor";

import { useDataProvider, useNotification } from "@refinedev/core";
import { useLocation, useNavigate } from "react-router-dom";

import { usePGBroadcastRefresh } from "../realtime/usePGBroadcastRefresh";

const PROJECT_SCHEMA = "project";
const IAM_SCHEMA = "iam";

const { Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

/**
 * UI Configuration Knobs
 */
const UI = {
  PAGE_MIN_WIDTH_PX: 320,
  SEARCH_MAX_WIDTH_PX: 460,
  ENABLE_TAB_SCROLL: true,
  ENABLE_MOBILE_TABLE_SCROLL: false,
  DRAWER_MOBILE_WIDTH: "100%" as const,
  MODAL_DESKTOP_WIDTH_PX: 600,
};

// --- Helpers ---

function useIsMobile() {
  const screens = useBreakpoint();
  return !screens.sm;
}

const TopFocusAnchor: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      ref.current?.focus({ preventScroll: false });
    }, 50);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      style={{ outline: "none", height: 0, width: 0, overflow: "hidden" }}
      aria-hidden="true"
    />
  );
};

/**
 * Reusable Markdown Editor with Dropdown Mode Switcher
 * Modes: Rich | Raw | Preview | Raw + Preview
 */
interface MarkdownEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  rows?: number;
  placeholder?: string;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value = "",
  onChange,
  readOnly,
  rows = 5,
  placeholder,
}) => {
  const { token } = theme.useToken();

  // Modes: "rich" | "raw" | "preview" | "raw-preview"
  const [mode, setMode] = useState<string>("rich");

  const effectiveMode = readOnly ? "preview" : mode;

  const showRich = effectiveMode === "rich";
  const showRaw = effectiveMode === "raw" || effectiveMode === "raw-preview";
  const showPreview =
    effectiveMode === "preview" || effectiveMode === "raw-preview";

  const isDark = useMemo(() => {
    return (
      token.colorBgContainer === "#141414" ||
      token.colorBgContainer === "#000000" ||
      document.body.classList.contains("app-dark")
    );
  }, [token]);

  const minHeight = rows * 24;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {!readOnly && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Select
            value={mode}
            onChange={(v) => setMode(v)}
            size="small"
            style={{ width: 160 }}
            options={[
              { label: "Rich Editor", value: "rich" },
              { label: "Raw Markdown", value: "raw" },
              { label: "Raw + Preview", value: "raw-preview" },
              { label: "Preview Only", value: "preview" },
            ]}
          />
        </div>
      )}

      <div
        style={{
          border: `1px solid ${token.colorBorder}`,
          borderRadius: token.borderRadius,
          backgroundColor: showRaw ? token.colorBgContainer : "transparent",
          overflow: "hidden",
        }}
      >
        {/* RAW Input */}
        {showRaw && (
          <TextArea
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            rows={rows}
            placeholder={placeholder || "Markdown supported..."}
            style={{
              fontFamily: "monospace",
              border: "none",
              borderRadius: 0,
              boxShadow: "none",
              resize: "vertical",
              minHeight: minHeight,
              backgroundColor: "transparent",
              color: token.colorText,
              borderBottom: showPreview
                ? `1px solid ${token.colorBorderSecondary}`
                : "none",
            }}
          />
        )}

        {/* RICH Editor */}
        {showRich && (
          <div
            className={isDark ? "dark-theme" : ""}
            style={
              {
                minHeight: minHeight,
                backgroundColor: token.colorBgContainer,
                "--mdx-base-color": token.colorText,
                "--mdx-text-color": token.colorText,
                "--mdx-bg-color": token.colorBgContainer,
                "--mdx-border-color": token.colorBorder,
                "--mdx-toolbar-bg-color": token.colorFillQuaternary,
                "--mdx-toolbar-text-color": token.colorTextDescription,
                "--mdx-toolbar-active-bg-color": token.colorFillSecondary,
                "--mdx-toolbar-hover-bg-color": token.colorFillTertiary,
                "--mdx-dropdown-bg-color": token.colorBgElevated,
                "--mdx-dropdown-text-color": token.colorText,
              } as React.CSSProperties
            }
          >
            <MDXEditor
              markdown={value}
              onChange={(v) => onChange?.(v)}
              placeholder={placeholder}
              contentEditableClassName="prose"
              plugins={[
                headingsPlugin(),
                listsPlugin(),
                quotePlugin(),
                thematicBreakPlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                markdownShortcutPlugin(),
                toolbarPlugin({
                  toolbarContents: () => (
                    <>
                      <UndoRedo />
                      <BoldItalicUnderlineToggles />
                      <ListsToggle />
                      <CreateLink />
                      <InsertThematicBreak />
                    </>
                  ),
                }),
              ]}
            />
          </div>
        )}

        {/* PREVIEW Pane */}
        {showPreview && (
          <div
            style={{
              padding: "12px",
              minHeight: showRaw ? 120 : minHeight,
              maxHeight: 500,
              overflowY: "auto",
              backgroundColor: token.colorFillQuaternary,
              color: token.colorText,
            }}
          >
            {effectiveMode === "raw-preview" && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  marginBottom: 8,
                  textTransform: "uppercase",
                  color: token.colorTextDescription,
                  opacity: 0.8,
                }}
              >
                Live Preview
              </div>
            )}

            {value ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ node, ...props }) => (
                    <p style={{ color: token.colorText }} {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <li style={{ color: token.colorText }} {...props} />
                  ),
                  h1: ({ node, ...props }) => (
                    <h1 style={{ color: token.colorTextHeading }} {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 style={{ color: token.colorTextHeading }} {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 style={{ color: token.colorTextHeading }} {...props} />
                  ),
                  a: ({ node, ...props }) => (
                    <a style={{ color: token.colorLink }} {...props} />
                  ),
                }}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <span
                style={{
                  color: token.colorTextPlaceholder,
                  fontStyle: "italic",
                }}
              >
                Nothing to preview
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Reusable wrapper that handles Mobile Drawer vs Desktop Modal logic
 */
interface ResponsiveEditorProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  footer?: React.ReactNode;
  onOk?: () => void;
  okText?: string;
  cancelText?: string;
  children: React.ReactNode;
  width?: number | string;
}

const ResponsiveEditor: React.FC<ResponsiveEditorProps> = ({
  open,
  onClose,
  title,
  footer,
  onOk,
  okText = "Save",
  cancelText = "Cancel",
  children,
  width = UI.MODAL_DESKTOP_WIDTH_PX,
}) => {
  const isMobile = useIsMobile();

  const content = (
    <>
      <TopFocusAnchor />
      {children}
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        title={title}
        width={UI.DRAWER_MOBILE_WIDTH}
        placement="left"
        destroyOnClose
        extra={
          footer !== null && (
            <Space>
              <Button onClick={onClose}>{cancelText}</Button>
              {onOk && (
                <Button type="primary" onClick={onOk}>
                  {okText}
                </Button>
              )}
            </Space>
          )
        }
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={width}
      destroyOnClose
      onOk={onOk}
      okText={okText}
      cancelText={cancelText}
      footer={footer}
      styles={{ body: { maxHeight: "80vh", overflowY: "auto" } }}
    >
      {content}
    </Modal>
  );
};

// --- Utils ---

function useQueryParams() {
  const loc = useLocation();
  return React.useMemo(() => new URLSearchParams(loc.search), [loc]);
}

function filterRows<T extends Record<string, any>>(rows: T[], search: string) {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    Object.values(row).some(
      (v) => v != null && String(v).toLowerCase().includes(q),
    ),
  );
}

type LookupRow = {
  id: number;
  code: string;
  label: string;
  sort_order?: number;
  is_active?: boolean;
};

function labelFor(id: any, rows: LookupRow[], fallback?: string) {
  if (id == null) return fallback || "";
  const hit = rows.find((r) => Number(r.id) === Number(id));
  return hit?.label ?? fallback ?? String(id);
}

function defaultIdByCodeOrFirst(
  rows: LookupRow[],
  preferredCode: string,
  fallbackId: number,
) {
  if (!rows?.length) return fallbackId;
  const byCode = rows.find((r) => r.code === preferredCode);
  if (byCode) return Number(byCode.id);

  const sorted = [...rows].sort((a, b) => {
    const ao = a.sort_order ?? 100;
    const bo = b.sort_order ?? 100;
    if (ao !== bo) return ao - bo;
    return Number(a.id) - Number(b.id);
  });
  return Number(sorted[0].id);
}

type RevisionRow = {
  id: string;
  revision_no: number;
  action: "INSERT" | "UPDATE" | "DELETE";
  changed_at: string;
  changed_by: string | null;
  row_old: any | null;
  row_new: any | null;
};

// --- History Hook ---

function useRevisionHistory({
  dataProvider,
  modal,
  notify,
  onRestored,
}: {
  dataProvider: any;
  modal: any;
  notify?: (o: any) => void;
  onRestored?: () => void;
}) {
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [historyType, setHistoryType] = React.useState<
    "project" | "task" | "comment"
  >("project");
  const [historyEntityId, setHistoryEntityId] = React.useState<string>("");
  const [historyTitle, setHistoryTitle] = React.useState<string>("");
  const [historyRows, setHistoryRows] = React.useState<RevisionRow[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const [revisionOpen, setRevisionOpen] = React.useState(false);
  const [revisionRow, setRevisionRow] = React.useState<RevisionRow | null>(
    null,
  );
  const [restoreLoading, setRestoreLoading] = React.useState(false);

  const notifyOk = (msg: string) => {
    if (notify) notify({ type: "success", message: msg });
    else message.success(msg);
  };

  const notifyErr = (err: any, fallback = "Action failed") => {
    const msg = err?.message || fallback;
    const details = err?.body?.details || err?.body?.hint;
    if (notify) notify({ type: "error", message: msg, description: details });
    else message.error(msg);
  };

  const openHistory = React.useCallback(
    async (
      type: "project" | "task" | "comment",
      entityId: string,
      title: string,
    ) => {
      if (!dataProvider?.getList) return;
      setHistoryType(type);
      setHistoryEntityId(entityId);
      setHistoryTitle(title);
      setHistoryRows([]);
      setHistoryOpen(true);
      setHistoryLoading(true);

      try {
        let resource = "";
        let field = "";
        if (type === "project") {
          resource = "project_revisions";
          field = "project_id";
        } else if (type === "task") {
          resource = "task_revisions";
          field = "task_id";
        } else {
          resource = "task_comment_revisions";
          field = "comment_id";
        }

        const res = await dataProvider.getList({
          resource,
          pagination: { current: 1, pageSize: 200 },
          sorters: [{ field: "revision_no", order: "desc" }],
          filters: [{ field, operator: "eq", value: entityId }],
          meta: { profile: PROJECT_SCHEMA },
        });

        setHistoryRows((res.data || []) as RevisionRow[]);
      } catch (err: any) {
        notifyErr(err, "Failed to load revision history");
      } finally {
        setHistoryLoading(false);
      }
    },
    [dataProvider],
  );

  const restoreThisRevision = async () => {
    if (!revisionRow) return;
    const rev = revisionRow.revision_no;
    const type = historyType;
    const entityId = historyEntityId;
    const fn =
      type === "project"
        ? "restore_project_revision"
        : type === "task"
          ? "restore_task_revision"
          : "restore_task_comment_revision";

    const payload =
      type === "project"
        ? { p_project_id: entityId, p_revision_no: rev }
        : type === "task"
          ? { p_task_id: entityId, p_revision_no: rev }
          : { p_comment_id: entityId, p_revision_no: rev };

    modal.confirm({
      title: `Restore ${type} to revision #${rev}?`,
      content:
        "This overwrites the current row with the saved revision state (safe allowlist).",
      okText: "Restore",
      okButtonProps: { danger: true },
      onOk: async () => {
        setRestoreLoading(true);
        try {
          if (!dataProvider?.custom)
            throw new Error("Custom RPC not supported");
          await dataProvider.custom({
            url: `/rpc/${fn}`,
            method: "POST",
            data: payload,
            meta: { profile: PROJECT_SCHEMA },
          });

          notifyOk(`Restored to revision #${rev}`);
          setRevisionOpen(false);
          setRevisionRow(null);
          setHistoryOpen(false);
          setHistoryRows([]);
          setHistoryEntityId("");
          onRestored?.();
        } catch (err: any) {
          notifyErr(err, "Restore failed");
        } finally {
          setRestoreLoading(false);
        }
      },
    });
  };

  const HistoryModals = (
    <>
      <ResponsiveEditor
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={`History: ${historyTitle}`}
        okText="Close"
        onOk={() => setHistoryOpen(false)}
        cancelText="Close"
        footer={null}
        width={920}
      >
        <Table
          size="small"
          rowKey="id"
          loading={historyLoading}
          dataSource={historyRows}
          pagination={{ pageSize: 20 }}
          scroll={{ x: UI.ENABLE_MOBILE_TABLE_SCROLL ? 800 : undefined }}
          columns={[
            { title: "Rev", dataIndex: "revision_no", width: 60 },
            { title: "Action", dataIndex: "action", width: 90 },
            { title: "When", dataIndex: "changed_at", width: 200 },
            {
              title: "Who",
              dataIndex: "changed_by",
              render: (v) => v || <span style={{ opacity: 0.6 }}>—</span>,
            },
            {
              title: "Actions",
              width: 80,
              render: (_, row) => (
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    setRevisionRow(row);
                    setRevisionOpen(true);
                  }}
                />
              ),
            },
          ]}
        />
      </ResponsiveEditor>

      <ResponsiveEditor
        open={revisionOpen}
        onClose={() => setRevisionOpen(false)}
        title={
          revisionRow ? `Revision #${revisionRow.revision_no}` : "Revision"
        }
        width={980}
        footer={
          <Space>
            <Button onClick={() => setRevisionOpen(false)}>Close</Button>
            <Button
              danger
              icon={<RollbackOutlined />}
              loading={restoreLoading}
              disabled={!revisionRow}
              onClick={restoreThisRevision}
            >
              Restore this revision
            </Button>
          </Space>
        }
      >
        {revisionRow && (
          <>
            <Text>
              <b>When:</b> {revisionRow.changed_at} | <b>Who:</b>{" "}
              {revisionRow.changed_by || "—"}
            </Text>
            <Divider style={{ margin: "8px 0" }} />

            <Text strong>Old Data</Text>
            <pre
              style={{
                background: "#f5f5f5",
                padding: 8,
                borderRadius: 4,
                overflow: "auto",
                maxHeight: 200,
              }}
            >
              {JSON.stringify(revisionRow.row_old, null, 2)}
            </pre>

            <Text strong>New Data</Text>
            <pre
              style={{
                background: "#f5f5f5",
                padding: 8,
                borderRadius: 4,
                overflow: "auto",
                maxHeight: 200,
              }}
            >
              {JSON.stringify(revisionRow.row_new, null, 2)}
            </pre>
          </>
        )}
      </ResponsiveEditor>
    </>
  );

  return { openHistory, HistoryModals };
}

// --- Main Component ---

export const ProjectControl: React.FC = () => {
  const isMobile = useIsMobile();
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open } = useNotification();
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();

  const q = useQueryParams();
  const projectId = q.get("project_id") || "";
  const taskId = q.get("task_id") || "";

  const [activeTab, setActiveTab] = React.useState("projects");
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  // Data
  const [projects, setProjects] = React.useState<any[]>([]);
  const [tasks, setTasks] = React.useState<any[]>([]);
  const [comments, setComments] = React.useState<any[]>([]);
  const [projectShares, setProjectShares] = React.useState<any[]>([]);
  const [taskShares, setTaskShares] = React.useState<any[]>([]);

  // IAM Lookups
  const [groups, setGroups] = React.useState<any[]>([]);

  // Lookups
  const [taskStatuses, setTaskStatuses] = React.useState<LookupRow[]>([]);
  const [taskPriorities, setTaskPriorities] = React.useState<LookupRow[]>([]);
  const [projectStatuses, setProjectStatuses] = React.useState<LookupRow[]>([]);
  const [projectPriorities, setProjectPriorities] = React.useState<LookupRow[]>(
    [],
  );

  // Selected Names
  const [selectedProjectName, setSelectedProjectName] = React.useState("");
  const [selectedTaskName, setSelectedTaskName] = React.useState("");

  const [refreshKey, setRefreshKey] = React.useState(0);

  const notifySuccess = (msg: string) => {
    if (open) open({ type: "success", message: msg });
    else message.success(msg);
  };

  const notifyError = (err: any, fallback = "Action failed") => {
    const msg = err?.message || fallback;
    const details = err?.body?.details || err?.body?.hint;
    if (open) open({ type: "error", message: msg, description: details });
    else message.error(msg);
  };

  const { openHistory, HistoryModals } = useRevisionHistory({
    dataProvider,
    modal,
    notify: open,
    onRestored: () => setRefreshKey((k) => k + 1),
  });

  // --- Effects ---

  useEffect(() => {
    if (taskId) setActiveTab("comments");
    else if (projectId) setActiveTab("tasks");
  }, [taskId, projectId]);

  const loadGroups = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    try {
      const res = await dataProvider.getList({
        resource: "groups",
        pagination: { current: 1, pageSize: 500 },
        sorters: [{ field: "name", order: "asc" }],
        meta: { profile: IAM_SCHEMA },
      });
      setGroups(res.data || []);
    } catch (err: any) {
      // don't hard-fail the page if groups aren't available; but show a useful error
      notifyError(err, "Failed to load groups (iam.groups)");
    }
  }, [dataProvider]);

  const loadLookups = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    try {
      const [ts, tp, ps, pp] = await Promise.all([
        dataProvider.getList({
          resource: "task_status_types",
          meta: { profile: PROJECT_SCHEMA },
        }),
        dataProvider.getList({
          resource: "task_priority_types",
          meta: { profile: PROJECT_SCHEMA },
        }),
        dataProvider.getList({
          resource: "project_status_types",
          meta: { profile: PROJECT_SCHEMA },
        }),
        dataProvider.getList({
          resource: "project_priority_types",
          meta: { profile: PROJECT_SCHEMA },
        }),
      ]);
      setTaskStatuses(ts.data || []);
      setTaskPriorities(tp.data || []);
      setProjectStatuses(ps.data || []);
      setProjectPriorities(pp.data || []);
    } catch (err: any) {
      notifyError(err, "Failed to load lookups");
    }
  }, [dataProvider]);

  const loadCore = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    try {
      const [pRes, tRes] = await Promise.all([
        dataProvider.getList({
          resource: "projects",
          pagination: { current: 1, pageSize: 500 },
          sorters: [{ field: "updated_at", order: "desc" }],
          meta: { profile: PROJECT_SCHEMA },
        }),
        dataProvider.getList({
          resource: "tasks",
          pagination: { current: 1, pageSize: 1000 },
          sorters: [{ field: "updated_at", order: "desc" }],
          meta: { profile: PROJECT_SCHEMA },
        }),
      ]);
      setProjects(pRes.data || []);
      setTasks(tRes.data || []);
    } catch (err: any) {
      notifyError(err, "Failed to load core data");
    }
  }, [dataProvider]);

  useEffect(() => {
    loadGroups();
    loadLookups();
    loadCore();
  }, [loadGroups, loadLookups, loadCore]);

  useEffect(() => {
    if (!projectId) {
      setSelectedProjectName("");
      return;
    }
    dataProvider
      .getOne({
        resource: "projects",
        id: projectId,
        meta: { profile: PROJECT_SCHEMA },
      })
      .then((res) => setSelectedProjectName(res.data.name))
      .catch(() => setSelectedProjectName(""));
  }, [projectId, dataProvider, refreshKey]);

  useEffect(() => {
    if (!taskId) {
      setSelectedTaskName("");
      return;
    }
    dataProvider
      .getOne({
        resource: "tasks",
        id: taskId,
        meta: { profile: PROJECT_SCHEMA },
      })
      .then((res) => setSelectedTaskName(res.data.name))
      .catch(() => setSelectedTaskName(""));
  }, [taskId, dataProvider, refreshKey]);

  const loadTabData = React.useCallback(
    async (tab: string) => {
      if (!dataProvider?.getList) return;
      setLoading(true);
      try {
        let resource = "";
        let filters: any[] = [];
        let sorters: any[] = [{ field: "created_at", order: "desc" }];

        if (tab === "comments") {
          resource = "task_comments";
          if (taskId)
            filters = [{ field: "task_id", operator: "eq", value: taskId }];
        } else if (tab === "projectShares") {
          resource = "project_shares";
          // strongly prefer filtering to selected project to avoid showing a huge list and to match intent
          if (projectId)
            filters = [
              { field: "project_id", operator: "eq", value: projectId },
            ];
        } else if (tab === "taskShares") {
          resource = "task_shares";
          if (taskId)
            filters = [{ field: "task_id", operator: "eq", value: taskId }];
        }

        if (resource) {
          const res = await dataProvider.getList({
            resource,
            pagination: { current: 1, pageSize: 500 },
            sorters,
            filters,
            meta: { profile: PROJECT_SCHEMA },
          });

          if (tab === "comments") setComments(res.data || []);
          else if (tab === "projectShares") setProjectShares(res.data || []);
          else if (tab === "taskShares") setTaskShares(res.data || []);
        }
      } catch (err: any) {
        notifyError(err, "Failed to load tab data");
      } finally {
        setLoading(false);
      }
    },
    [dataProvider, projectId, taskId],
  );

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData, refreshKey]);


  // When refreshKey changes, also reload core lists (projects/tasks)
  useEffect(() => {
    loadCore();
  }, [loadCore, refreshKey]);


  // ✅ Subscribe only to what we need (avoids unnecessary noise)
  const realtimeTopics = React.useMemo(() => {
    const base = [
      "project.projects",
      "project.tasks",
    ];

    if (activeTab === "comments") base.push("project.task_comments");
    if (activeTab === "projectShares") base.push("project.project_shares");
    if (activeTab === "taskShares") base.push("project.task_shares");

    // Optional: if you want “detail” panels to refresh even when not on that tab
    // but only when an entity is selected, uncomment:
    //
    // if (taskId) base.push("project.task_comments", "project.task_shares");
    // if (projectId) base.push("project.project_shares");

    return base;
  }, [activeTab /*, projectId, taskId */]);

  usePGBroadcastRefresh({
    topics: realtimeTopics,
    debounceMs: 150,
    enabled: true,
    onChange: () => {
      // bump refreshKey; your existing effects will refresh relevant data
      setRefreshKey((k) => k + 1);
    },
  });


/*
  const reloadCurrent = async () => {
    await loadCore();
    await loadTabData(activeTab);
  };
*/

  const reloadCurrent = React.useCallback(async () => {
    setRefreshKey((k) => k + 1);
  }, []);


  // --- Nav ---
  const setProjectParam = (pid?: string) => {
    const p = new URLSearchParams(window.location.search);
    if (pid) p.set("project_id", pid);
    else p.delete("project_id");
    p.delete("task_id");
    navigate({ search: p.toString() });
  };

  const setTaskParam = (tid?: string) => {
    const p = new URLSearchParams(window.location.search);
    if (projectId) p.set("project_id", projectId);
    if (tid) p.set("task_id", tid);
    else p.delete("task_id");
    navigate({ search: p.toString() });
  };

  // --- Filtering ---
  const filteredProjects = filterRows(projects, search);
  const filteredTasks = filterRows(
    projectId ? tasks.filter((t) => t.project_id === projectId) : tasks,
    search,
  );
  const filteredComments = filterRows(
    taskId ? comments.filter((c) => c.task_id === taskId) : comments,
    search,
  );

  const filteredProjectShares = filterRows(projectShares, search);
  const filteredTaskShares = filterRows(taskShares, search);

  // --- Overlays State ---
  const [projectOverlayOpen, setProjectOverlayOpen] = React.useState(false);
  const [projectMode, setProjectMode] = React.useState<"view" | "edit">("edit");
  const [activeProject, setActiveProject] = React.useState<any | null>(null);

  const [taskOverlayOpen, setTaskOverlayOpen] = React.useState(false);
  const [taskMode, setTaskMode] = React.useState<"view" | "edit">("edit");
  const [activeTask, setActiveTask] = React.useState<any | null>(null);

  const [commentOverlayOpen, setCommentOverlayOpen] = React.useState(false);
  const [commentMode, setCommentMode] = React.useState<"view" | "edit">("edit");
  const [activeComment, setActiveComment] = React.useState<any | null>(null);

  // Shares overlays
  const [projectShareOverlayOpen, setProjectShareOverlayOpen] =
    React.useState(false);
  const [projectShareMode, setProjectShareMode] = React.useState<
    "view" | "edit"
  >("edit");
  const [activeProjectShare, setActiveProjectShare] = React.useState<
    any | null
  >(null);

  const [taskShareOverlayOpen, setTaskShareOverlayOpen] = React.useState(false);
  const [taskShareMode, setTaskShareMode] = React.useState<"view" | "edit">(
    "edit",
  );
  const [activeTaskShare, setActiveTaskShare] = React.useState<any | null>(
    null,
  );

  const [projectForm] = Form.useForm();
  const [taskForm] = Form.useForm();
  const [commentForm] = Form.useForm();

  const [projectShareForm] = Form.useForm();
  const [taskShareForm] = Form.useForm();

  // --- Overlay Actions ---

  const openProject = (rec: any, mode: "view" | "edit") => {
    setActiveProject(rec);
    setProjectMode(mode);
    projectForm.setFieldsValue({
      name: rec?.name ?? "",
      description: rec?.description ?? "",
      status_id:
        rec?.status_id ?? defaultIdByCodeOrFirst(projectStatuses, "to_do", 2),
      priority_id:
        rec?.priority_id ??
        defaultIdByCodeOrFirst(projectPriorities, "no_priority", 1),
    });
    setProjectOverlayOpen(true);
  };

  const openTask = (rec: any, mode: "view" | "edit") => {
    setActiveTask(rec);
    setTaskMode(mode);
    taskForm.setFieldsValue({
      project_id: (rec?.project_id ?? projectId) || undefined,
      name: rec?.name ?? "",
      description: rec?.description ?? "",
      status_id:
        rec?.status_id ?? defaultIdByCodeOrFirst(taskStatuses, "to_do", 2),
      priority_id:
        rec?.priority_id ??
        defaultIdByCodeOrFirst(taskPriorities, "no_priority", 1),
    });
    setTaskOverlayOpen(true);
  };

  const openComment = (rec: any, mode: "view" | "edit") => {
    setActiveComment(rec);
    setCommentMode(mode);
    commentForm.setFieldsValue({
      body: rec?.body ?? "",
      task_id: (rec?.task_id ?? taskId) || "",
    });
    setCommentOverlayOpen(true);
  };

  const groupLabelById = (gid: string) => {
    const g = groups.find((x) => x.id === gid);
    return g?.name || g?.label || gid;
  };

  const openProjectShare = (rec: any, mode: "view" | "edit") => {
    // rec may be __new__
    const pid = rec?.project_id ?? projectId;
    setActiveProjectShare(rec);
    setProjectShareMode(mode);
    projectShareForm.setFieldsValue({
      project_id: pid || undefined,
      group_id: rec?.group_id ?? undefined,
      can_create: rec?.can_create ?? true,
      can_read: rec?.can_read ?? true,
      can_update: rec?.can_update ?? false,
      can_delete: rec?.can_delete ?? false,
      can_admin: rec?.can_admin ?? false,
    });
    setProjectShareOverlayOpen(true);
  };

  const openTaskShare = (rec: any, mode: "view" | "edit") => {
    const tid = rec?.task_id ?? taskId;
    setActiveTaskShare(rec);
    setTaskShareMode(mode);
    taskShareForm.setFieldsValue({
      task_id: tid || undefined,
      group_id: rec?.group_id ?? undefined,
      can_create: rec?.can_create ?? true,
      can_read: rec?.can_read ?? true,
      can_update: rec?.can_update ?? false,
      can_delete: rec?.can_delete ?? false,
    });
    setTaskShareOverlayOpen(true);
  };

  const handleSaveProject = async () => {
    if (!activeProject) return;
    try {
      const vals = await projectForm.validateFields();
      const vars = { ...vals, description: vals.description || null };

      if (activeProject.id === "__new__") {
        await dataProvider.create({
          resource: "projects",
          variables: vars,
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Project created");
      } else {
        await dataProvider.update({
          resource: "projects",
          id: activeProject.id,
          variables: vars,
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Project updated");
      }
      setProjectOverlayOpen(false);
      setActiveProject(null);
      await reloadCurrent();
    } catch (e: any) {
      notifyError(e);
    }
  };

  const handleSaveTask = async () => {
    if (!activeTask) return;
    try {
      const vals = await taskForm.validateFields();
      const pid = vals.project_id || projectId;
      if (!pid) {
        notifyError(null, "Project required");
        return;
      }
      const vars = {
        ...vals,
        project_id: pid,
        description: vals.description || null,
      };

      if (activeTask.id === "__new__") {
        await dataProvider.create({
          resource: "tasks",
          variables: vars,
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Task created");
      } else {
        await dataProvider.update({
          resource: "tasks",
          id: activeTask.id,
          variables: vars,
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Task updated");
      }
      setTaskOverlayOpen(false);
      setActiveTask(null);
      await reloadCurrent();
    } catch (e: any) {
      notifyError(e);
    }
  };

  const handleSaveComment = async () => {
    if (!activeComment) return;
    try {
      const vals = await commentForm.validateFields();
      const body = (vals.body ?? "").trim();
      const tid = vals.task_id || taskId;
      if (!tid) {
        notifyError(null, "Task ID required");
        return;
      }

      if (activeComment.id === "__new__") {
        await dataProvider.create({
          resource: "task_comments",
          variables: { task_id: tid, body },
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Comment created");
      } else {
        await dataProvider.update({
          resource: "task_comments",
          id: activeComment.id,
          variables: { body },
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Comment updated");
      }
      setCommentOverlayOpen(false);
      setActiveComment(null);
      await loadTabData("comments");
    } catch (e: any) {
      notifyError(e);
    }
  };

  const handleSaveProjectShare = async () => {
    if (!activeProjectShare) return;
    try {
      const vals = await projectShareForm.validateFields();
      const pid = vals.project_id || projectId;
      if (!pid) {
        notifyError(null, "Project required (select a project first)");
        return;
      }
      const vars = {
        project_id: pid,
        group_id: vals.group_id,
        can_create: !!vals.can_create,
        can_read: !!vals.can_read,
        can_update: !!vals.can_update,
        can_delete: !!vals.can_delete,
        can_admin: !!vals.can_admin,
      };

      if (activeProjectShare.id === "__new__") {
        await dataProvider.create({
          resource: "project_shares",
          variables: vars,
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Project share added");
      } else {
        await dataProvider.update({
          resource: "project_shares",
          id: activeProjectShare.id,
          variables: vars,
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Project share updated");
      }

      setProjectShareOverlayOpen(false);
      setActiveProjectShare(null);
      await loadTabData("projectShares");
    } catch (e: any) {
      notifyError(e);
    }
  };

  const handleSaveTaskShare = async () => {
    if (!activeTaskShare) return;
    try {
      const vals = await taskShareForm.validateFields();
      const tid = vals.task_id || taskId;
      if (!tid) {
        notifyError(null, "Task required (select a task first)");
        return;
      }
      const vars = {
        task_id: tid,
        group_id: vals.group_id,
        can_create: !!vals.can_create,
        can_read: !!vals.can_read,
        can_update: !!vals.can_update,
        can_delete: !!vals.can_delete,
      };

      if (activeTaskShare.id === "__new__") {
        await dataProvider.create({
          resource: "task_shares",
          variables: vars,
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Task share added");
      } else {
        await dataProvider.update({
          resource: "task_shares",
          id: activeTaskShare.id,
          variables: vars,
          meta: { profile: PROJECT_SCHEMA },
        });
        notifySuccess("Task share updated");
      }

      setTaskShareOverlayOpen(false);
      setActiveTaskShare(null);
      await loadTabData("taskShares");
    } catch (e: any) {
      notifyError(e);
    }
  };

  const handleDelete = (resource: string, id: string, name: string) => {
    modal.confirm({
      title: `Delete ${name}?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await dataProvider.deleteOne({
            resource,
            id,
            meta: { profile: PROJECT_SCHEMA },
          });
          notifySuccess("Deleted");
          if (resource === "projects" && projectId === id)
            setProjectParam(undefined);
          if (resource === "tasks" && taskId === id) setTaskParam(undefined);
          await reloadCurrent();
        } catch (e) {
          notifyError(e);
        }
      },
    });
  };

  const handleDeleteShare = (
    resource: "project_shares" | "task_shares",
    id: string,
    label: string,
  ) => {
    modal.confirm({
      title: `Remove share?`,
      content: label,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await dataProvider.deleteOne({
            resource,
            id,
            meta: { profile: PROJECT_SCHEMA },
          });
          notifySuccess("Share removed");
          await loadTabData(
            resource === "project_shares" ? "projectShares" : "taskShares",
          );
        } catch (e) {
          notifyError(e);
        }
      },
    });
  };

  // --- Scroll Props Helper ---
  const getTableScroll = (width: number) => {
    if (isMobile && !UI.ENABLE_MOBILE_TABLE_SCROLL) return undefined;
    return { x: width };
  };

  const groupOptions = useMemo(
    () =>
      (groups || []).map((g) => ({
        label: g.name ?? g.label ?? g.id,
        value: g.id,
      })),
    [groups],
  );

  // --- Render ---
  return (
    <div style={{ minWidth: UI.PAGE_MIN_WIDTH_PX, padding: 16 }}>
      {contextHolder}
      {/* Row 1: Title */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 20, lineHeight: 1.2 }}>
          {activeTab.charAt(0).toUpperCase() +
            activeTab.slice(1).replace(/([A-Z])/g, " $1")}
        </Text>
      </div>

      {/* Row 2: Controls */}
      <div style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            allowClear
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240, maxWidth: UI.SEARCH_MAX_WIDTH_PX }}
            suffix={
              search ? (
                <CloseCircleOutlined
                  onClick={() => setSearch("")}
                  style={{ cursor: "pointer" }}
                />
              ) : null
            }
          />
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={reloadCurrent}
              loading={loading}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Breadcrumb Tags */}
      {(projectId || taskId) && (
        <div style={{ marginBottom: 10 }}>
          <Space wrap>
            {projectId && (
              <Tag>
                Project: <b>{selectedProjectName}</b>
              </Tag>
            )}
            {taskId && (
              <Tag>
                Task: <b>{selectedTaskName}</b>
              </Tag>
            )}
          </Space>
        </div>
      )}

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ overflowX: UI.ENABLE_TAB_SCROLL ? "auto" : "visible" }}
        items={[
          { key: "projects", label: "Projects", icon: <FolderOutlined /> },
          { key: "tasks", label: "Tasks", icon: <ProfileOutlined /> },
          { key: "comments", label: "Comments", icon: <CommentOutlined /> },
          {
            key: "projectShares",
            label: "Project Shares",
            icon: <ShareAltOutlined />,
          },
          {
            key: "taskShares",
            label: "Task Shares",
            icon: <ShareAltOutlined />,
          },
        ]}
      />

      {/* Projects */}
      {activeTab === "projects" && (
        <>
          <div style={{ marginBottom: 8 }}>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openProject({ id: "__new__" }, "edit")}
            >
              New Project
            </Button>
          </div>
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filteredProjects}
            scroll={getTableScroll(1200)}
            columns={[
              {
                title: "",
                width: 44,
                fixed: "left",
                render: (_, r) => (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "view",
                          icon: <EyeOutlined />,
                          label: "View",
                          onClick: () => openProject(r, "view"),
                        },
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: "Edit",
                          onClick: () => openProject(r, "edit"),
                        },
                        {
                          key: "hist",
                          icon: <HistoryOutlined />,
                          label: "History",
                          onClick: () => openHistory("project", r.id, r.name),
                        },
                        {
                          key: "shares",
                          icon: <ShareAltOutlined />,
                          label: "Shares",
                          onClick: () => {
                            setProjectParam(r.id);
                            setActiveTab("projectShares");
                          },
                        },
                        { type: "divider" },
                        {
                          key: "del",
                          icon: <DeleteOutlined style={{ color: "red" }} />,
                          label: <span style={{ color: "red" }}>Delete</span>,
                          onClick: () =>
                            handleDelete("projects", r.id, "project"),
                        },
                      ],
                    }}
                  >
                    <Button size="small" type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                ),
              },
              {
                title: "Project",
                dataIndex: "name",
                width: 260,
                render: (v, r) => (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => {
                      setProjectParam(r.id);
                      setActiveTab("tasks");
                    }}
                  >
                    {v}
                  </Button>
                ),
              },
              { title: "Description", dataIndex: "description", width: 360 },
              {
                title: "Status",
                dataIndex: "status_id",
                width: 140,
                render: (v) => labelFor(v, projectStatuses),
              },
              {
                title: "Priority",
                dataIndex: "priority_id",
                width: 140,
                render: (v) => labelFor(v, projectPriorities),
              },
            ]}
          />
        </>
      )}

      {/* Tasks */}
      {activeTab === "tasks" && (
        <>
          <div style={{ marginBottom: 8 }}>
            <Space>
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openTask({ id: "__new__" }, "edit")}
              >
                New Task
              </Button>
              {projectId && (
                <Tag
                  closable
                  onClose={() => setProjectParam(undefined)}
                  color="blue"
                >
                  Filtered to Project
                </Tag>
              )}
            </Space>
          </div>
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filteredTasks}
            scroll={getTableScroll(1280)}
            columns={[
              {
                title: "",
                width: 44,
                fixed: "left",
                render: (_, r) => (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "view",
                          icon: <EyeOutlined />,
                          label: "View",
                          onClick: () => openTask(r, "view"),
                        },
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: "Edit",
                          onClick: () => openTask(r, "edit"),
                        },
                        {
                          key: "hist",
                          icon: <HistoryOutlined />,
                          label: "History",
                          onClick: () => openHistory("task", r.id, r.name),
                        },
                        {
                          key: "comm",
                          icon: <CommentOutlined />,
                          label: "Comments",
                          onClick: () => {
                            setProjectParam(r.project_id);
                            setTaskParam(r.id);
                            setActiveTab("comments");
                          },
                        },
                        {
                          key: "shares",
                          icon: <ShareAltOutlined />,
                          label: "Shares",
                          onClick: () => {
                            setProjectParam(r.project_id);
                            setTaskParam(r.id);
                            setActiveTab("taskShares");
                          },
                        },
                        { type: "divider" },
                        {
                          key: "del",
                          icon: <DeleteOutlined style={{ color: "red" }} />,
                          label: <span style={{ color: "red" }}>Delete</span>,
                          onClick: () => handleDelete("tasks", r.id, "task"),
                        },
                      ],
                    }}
                  >
                    <Button size="small" type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                ),
              },
              {
                title: "Task",
                dataIndex: "name",
                width: 260,
                render: (v, r) => (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => {
                      setTaskParam(r.id);
                      setActiveTab("comments");
                    }}
                  >
                    {v}
                  </Button>
                ),
              },
              {
                title: "Project",
                dataIndex: "project_id",
                width: 240,
                render: (pid) =>
                  projects.find((p) => p.id === pid)?.name || pid,
              },
              {
                title: "Status",
                dataIndex: "status_id",
                width: 140,
                render: (v) => labelFor(v, taskStatuses),
              },
              {
                title: "Priority",
                dataIndex: "priority_id",
                width: 140,
                render: (v) => labelFor(v, taskPriorities),
              },
            ]}
          />
        </>
      )}

      {/* Comments */}
      {activeTab === "comments" && (
        <>
          <div style={{ marginBottom: 8 }}>
            <Space>
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                disabled={!taskId}
                onClick={() => openComment({ id: "__new__" }, "edit")}
              >
                New Comment
              </Button>
              {!taskId ? (
                <Text type="secondary">Select a task first.</Text>
              ) : (
                <Tag
                  closable
                  onClose={() => setTaskParam(undefined)}
                  color="blue"
                >
                  Filtered to Task
                </Tag>
              )}
            </Space>
          </div>
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filteredComments}
            scroll={getTableScroll(1000)}
            columns={[
              {
                title: "",
                width: 44,
                fixed: "left",
                render: (_, r) => (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "view",
                          icon: <EyeOutlined />,
                          label: "View",
                          onClick: () => openComment(r, "view"),
                        },
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: "Edit",
                          onClick: () => openComment(r, "edit"),
                        },
                        {
                          key: "hist",
                          icon: <HistoryOutlined />,
                          label: "History",
                          onClick: () =>
                            openHistory("comment", r.id, "Comment"),
                        },
                      ],
                    }}
                  >
                    <Button size="small" type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                ),
              },
              {
                title: "Comment",
                dataIndex: "body",
                width: 500,
                render: (v) => (
                  <div style={{ maxHeight: 100, overflow: "hidden" }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {v}
                    </ReactMarkdown>
                  </div>
                ),
              },
              {
                title: "Task",
                dataIndex: "task_id",
                width: 240,
                render: (tid) => tasks.find((t) => t.id === tid)?.name || tid,
              },
              { title: "Created", dataIndex: "created_at", width: 200 },
            ]}
          />
        </>
      )}

      {/* Project Shares */}
      {activeTab === "projectShares" && (
        <>
          <div style={{ marginBottom: 8 }}>
            <Space wrap>
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                disabled={!projectId}
                onClick={() =>
                  openProjectShare(
                    { id: "__new__", project_id: projectId },
                    "edit",
                  )
                }
              >
                Add Project Share
              </Button>
              {!projectId ? (
                <Text type="secondary">
                  Select a project first (go to Projects and click one).
                </Text>
              ) : (
                <Tag color="blue">Filtered to Project</Tag>
              )}
            </Space>
          </div>

          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filteredProjectShares}
            scroll={getTableScroll(1100)}
            columns={[
              {
                title: "",
                width: 44,
                fixed: "left",
                render: (_, r) => (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "view",
                          icon: <EyeOutlined />,
                          label: "View",
                          onClick: () => openProjectShare(r, "view"),
                        },
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: "Edit",
                          onClick: () => openProjectShare(r, "edit"),
                        },
                        { type: "divider" },
                        {
                          key: "del",
                          icon: <DeleteOutlined style={{ color: "red" }} />,
                          label: <span style={{ color: "red" }}>Remove</span>,
                          onClick: () =>
                            handleDeleteShare(
                              "project_shares",
                              r.id,
                              `${groupLabelById(r.group_id)}`,
                            ),
                        },
                      ],
                    }}
                  >
                    <Button size="small" type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                ),
              },
              {
                title: "Group",
                dataIndex: "group_id",
                width: 320,
                render: (gid) => <span>{groupLabelById(gid)}</span>,
              },
              {
                title: "Create",
                dataIndex: "can_create",
                width: 90,
                render: (v) =>
                  v ? <Tag color="green">Yes</Tag> : <Tag>—</Tag>,
              },
              {
                title: "Read",
                dataIndex: "can_read",
                width: 90,
                render: (v) =>
                  v ? <Tag color="green">Yes</Tag> : <Tag>—</Tag>,
              },
              {
                title: "Update",
                dataIndex: "can_update",
                width: 90,
                render: (v) => (v ? <Tag color="gold">Yes</Tag> : <Tag>—</Tag>),
              },
              {
                title: "Delete",
                dataIndex: "can_delete",
                width: 90,
                render: (v) => (v ? <Tag color="red">Yes</Tag> : <Tag>—</Tag>),
              },
              {
                title: "Admin",
                dataIndex: "can_admin",
                width: 90,
                render: (v) => (v ? <Tag color="red">Yes</Tag> : <Tag>—</Tag>),
              },
              { title: "Created", dataIndex: "created_at", width: 200 },
            ]}
          />
        </>
      )}

      {/* Task Shares */}
      {activeTab === "taskShares" && (
        <>
          <div style={{ marginBottom: 8 }}>
            <Space wrap>
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                disabled={!taskId}
                onClick={() =>
                  openTaskShare({ id: "__new__", task_id: taskId }, "edit")
                }
              >
                Add Task Share
              </Button>
              {!taskId ? (
                <Text type="secondary">
                  Select a task first (go to Tasks and click one).
                </Text>
              ) : (
                <Tag color="blue">Filtered to Task</Tag>
              )}
            </Space>
          </div>

          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filteredTaskShares}
            scroll={getTableScroll(1100)}
            columns={[
              {
                title: "",
                width: 44,
                fixed: "left",
                render: (_, r) => (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "view",
                          icon: <EyeOutlined />,
                          label: "View",
                          onClick: () => openTaskShare(r, "view"),
                        },
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: "Edit",
                          onClick: () => openTaskShare(r, "edit"),
                        },
                        { type: "divider" },
                        {
                          key: "del",
                          icon: <DeleteOutlined style={{ color: "red" }} />,
                          label: <span style={{ color: "red" }}>Remove</span>,
                          onClick: () =>
                            handleDeleteShare(
                              "task_shares",
                              r.id,
                              `${groupLabelById(r.group_id)}`,
                            ),
                        },
                      ],
                    }}
                  >
                    <Button size="small" type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                ),
              },
              {
                title: "Group",
                dataIndex: "group_id",
                width: 320,
                render: (gid) => <span>{groupLabelById(gid)}</span>,
              },
              {
                title: "Create",
                dataIndex: "can_create",
                width: 90,
                render: (v) =>
                  v ? <Tag color="green">Yes</Tag> : <Tag>—</Tag>,
              },
              {
                title: "Read",
                dataIndex: "can_read",
                width: 90,
                render: (v) =>
                  v ? <Tag color="green">Yes</Tag> : <Tag>—</Tag>,
              },
              {
                title: "Update",
                dataIndex: "can_update",
                width: 90,
                render: (v) => (v ? <Tag color="gold">Yes</Tag> : <Tag>—</Tag>),
              },
              {
                title: "Delete",
                dataIndex: "can_delete",
                width: 90,
                render: (v) => (v ? <Tag color="red">Yes</Tag> : <Tag>—</Tag>),
              },
              { title: "Created", dataIndex: "created_at", width: 200 },
            ]}
          />
        </>
      )}

      {HistoryModals}

      {/* --- Unified Overlays via ResponsiveEditor --- */}

      {/* PROJECT */}
      <ResponsiveEditor
        open={projectOverlayOpen}
        onClose={() => setProjectOverlayOpen(false)}
        title={
          projectMode === "view"
            ? "View Project"
            : activeProject?.id === "__new__"
              ? "New Project"
              : "Edit Project"
        }
        onOk={projectMode === "edit" ? handleSaveProject : undefined}
        footer={projectMode === "view" ? null : undefined}
      >
        <Form form={projectForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input readOnly={projectMode === "view"} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <MarkdownEditor rows={4} readOnly={projectMode === "view"} />
          </Form.Item>
          <Form.Item
            name="status_id"
            label="Status"
            rules={[{ required: true }]}
          >
            <Select
              disabled={projectMode === "view"}
              options={projectStatuses
                .filter((s) => s.is_active !== false)
                .map((s) => ({ label: s.label, value: s.id }))}
            />
          </Form.Item>
          <Form.Item
            name="priority_id"
            label="Priority"
            rules={[{ required: true }]}
          >
            <Select
              disabled={projectMode === "view"}
              options={projectPriorities
                .filter((s) => s.is_active !== false)
                .map((s) => ({ label: s.label, value: s.id }))}
            />
          </Form.Item>
        </Form>
      </ResponsiveEditor>

      {/* TASK */}
      <ResponsiveEditor
        open={taskOverlayOpen}
        onClose={() => setTaskOverlayOpen(false)}
        title={
          taskMode === "view"
            ? "View Task"
            : activeTask?.id === "__new__"
              ? "New Task"
              : "Edit Task"
        }
        onOk={taskMode === "edit" ? handleSaveTask : undefined}
        footer={taskMode === "view" ? null : undefined}
      >
        <Form form={taskForm} layout="vertical">
          {!projectId && (
            <Form.Item
              name="project_id"
              label="Project"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                disabled={taskMode === "view"}
                options={projects.map((p) => ({ label: p.name, value: p.id }))}
              />
            </Form.Item>
          )}
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input readOnly={taskMode === "view"} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <MarkdownEditor rows={4} readOnly={taskMode === "view"} />
          </Form.Item>
          <Flex gap={12}>
            <Form.Item
              name="status_id"
              label="Status"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Select
                disabled={taskMode === "view"}
                options={taskStatuses
                  .filter((s) => s.is_active !== false)
                  .map((s) => ({ label: s.label, value: s.id }))}
              />
            </Form.Item>
            <Form.Item
              name="priority_id"
              label="Priority"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Select
                disabled={taskMode === "view"}
                options={taskPriorities
                  .filter((s) => s.is_active !== false)
                  .map((s) => ({ label: s.label, value: s.id }))}
              />
            </Form.Item>
          </Flex>
        </Form>
      </ResponsiveEditor>

      {/* COMMENT */}
      <ResponsiveEditor
        open={commentOverlayOpen}
        onClose={() => setCommentOverlayOpen(false)}
        title={
          commentMode === "view"
            ? "View Comment"
            : activeComment?.id === "__new__"
              ? "New Comment"
              : "Edit Comment"
        }
        onOk={commentMode === "edit" ? handleSaveComment : undefined}
        footer={commentMode === "view" ? null : undefined}
      >
        <Form form={commentForm} layout="vertical">
          <Form.Item name="body" label="Body" rules={[{ required: true }]}>
            <MarkdownEditor
              rows={8}
              readOnly={commentMode === "view"}
              placeholder="Type your comment..."
            />
          </Form.Item>
          {!taskId && (
            <Form.Item name="task_id" label="Task" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                disabled={commentMode === "view"}
                options={tasks.map((t) => ({ label: t.name, value: t.id }))}
              />
            </Form.Item>
          )}
        </Form>
      </ResponsiveEditor>

      {/* PROJECT SHARE */}
      <ResponsiveEditor
        open={projectShareOverlayOpen}
        onClose={() => setProjectShareOverlayOpen(false)}
        title={
          projectShareMode === "view"
            ? "View Project Share"
            : activeProjectShare?.id === "__new__"
              ? "Add Project Share"
              : "Edit Project Share"
        }
        onOk={projectShareMode === "edit" ? handleSaveProjectShare : undefined}
        footer={projectShareMode === "view" ? null : undefined}
        width={560}
      >
        <Form form={projectShareForm} layout="vertical">
          <Form.Item
            name="project_id"
            label="Project"
            rules={[{ required: true }]}
            initialValue={projectId || undefined}
          >
            <Select
              disabled={projectShareMode === "view" || !!projectId}
              options={projects.map((p) => ({ label: p.name, value: p.id }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item name="group_id" label="Group" rules={[{ required: true }]}>
            <Select
              disabled={projectShareMode === "view"}
              showSearch
              optionFilterProp="label"
              options={groupOptions}
              placeholder="Select a group to share with"
            />
          </Form.Item>

          <Divider style={{ margin: "8px 0" }} />
          <Text type="secondary">
            Tip: Update implies read; Delete implies write+read (your SQL
            function treats it that way for access checks).
          </Text>

          <div style={{ marginTop: 12 }}>
            <Space wrap>
              <Form.Item
                name="can_create"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={projectShareMode === "view"}>
                  Create
                </Checkbox>
              </Form.Item>
              <Form.Item
                name="can_read"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={projectShareMode === "view"}>Read</Checkbox>
              </Form.Item>
              <Form.Item
                name="can_update"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={projectShareMode === "view"}>
                  Update
                </Checkbox>
              </Form.Item>
              <Form.Item
                name="can_delete"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={projectShareMode === "view"}>
                  Delete
                </Checkbox>
              </Form.Item>
              <Form.Item
                name="can_admin"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={projectShareMode === "view"}>
                  Admin
                </Checkbox>
              </Form.Item>
            </Space>
          </div>
        </Form>
      </ResponsiveEditor>

      {/* TASK SHARE */}
      <ResponsiveEditor
        open={taskShareOverlayOpen}
        onClose={() => setTaskShareOverlayOpen(false)}
        title={
          taskShareMode === "view"
            ? "View Task Share"
            : activeTaskShare?.id === "__new__"
              ? "Add Task Share"
              : "Edit Task Share"
        }
        onOk={taskShareMode === "edit" ? handleSaveTaskShare : undefined}
        footer={taskShareMode === "view" ? null : undefined}
        width={560}
      >
        <Form form={taskShareForm} layout="vertical">
          <Form.Item
            name="task_id"
            label="Task"
            rules={[{ required: true }]}
            initialValue={taskId || undefined}
          >
            <Select
              disabled={taskShareMode === "view" || !!taskId}
              options={tasks.map((t) => ({ label: t.name, value: t.id }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item name="group_id" label="Group" rules={[{ required: true }]}>
            <Select
              disabled={taskShareMode === "view"}
              showSearch
              optionFilterProp="label"
              options={groupOptions}
              placeholder="Select a group to share with"
            />
          </Form.Item>

          <Divider style={{ margin: "8px 0" }} />
          <Text type="secondary">
            Tip: Update implies read; Delete implies write+read (your SQL
            function treats it that way for access checks).
          </Text>

          <div style={{ marginTop: 12 }}>
            <Space wrap>
              <Form.Item
                name="can_create"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={taskShareMode === "view"}>Create</Checkbox>
              </Form.Item>
              <Form.Item
                name="can_read"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={taskShareMode === "view"}>Read</Checkbox>
              </Form.Item>
              <Form.Item
                name="can_update"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={taskShareMode === "view"}>Update</Checkbox>
              </Form.Item>
              <Form.Item
                name="can_delete"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={taskShareMode === "view"}>Delete</Checkbox>
              </Form.Item>
            </Space>
          </div>
        </Form>
      </ResponsiveEditor>
    </div>
  );
};

export default ProjectControl;
